import { initSentry } from '@/sentry';
import { useCallback, useEffect, useRef } from 'react';

const TIMEOUT_MS = 7000;

/**
 * Listens for messages from the parent window, which provides the user info when
 * this UI app is embedded in an iframe. When the user info is received, it is
 * passed to the `onCredentials` callback.
 */
export const useParentWindowCredentials = (
  onCredentials: (metadata: object) => any,
  onTimeout?: () => any
) => {
  const timer = useRef<number | null>(null);

  // Responds to messages received from other windows (e.g., a parent or spawned child).
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data?.type === 'USER_CREDENTIALS') {
        onCredentials?.(event.data.data);

        // Clear any timer that was waiting for this info to arrive.
        if (timer.current) {
          window.clearTimeout(timer.current);
          timer.current = null;
        }
      }
    },
    [onCredentials]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window || (window.parent === window && !window.opener)) {
      // We are the top level window (not in an iframe) with no opener to provide us info.
      initSentry(false);
      return;
    }
    // Listen for messages from either a parent window or an opener window, providing us the user info.
    window.addEventListener('message', handleMessage);
    // If we were opened by a window.open() call, or inside the iframe of a parent window,
    // request the user info from our opener/parent window so that we can authenticate
    // without prompting the end-user with another Login UI.
    // If the opener/parent window is still open, it'll respond with a USER_CREDENTIALS message.
    const sourceWin = window.opener || window.parent;
    if (sourceWin && sourceWin !== window) {
      sourceWin.postMessage(
        {
          type: 'USER_CREDENTIALS_REQUESTED',
          data: '',
        },
        '*'
      );
      // Start a timer which will invoke the given callback if the answer doesn't arrive soon enough.
      if (onTimeout) {
        timer.current = window.setTimeout(onTimeout, TIMEOUT_MS);
      }
    } else {
      initSentry(false);
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);
};
